import type { NodeData, NodeProps, NodeType } from './types.ts';
import { newId, parseColor, solid } from './util.ts';

export const DEFAULT_FONT = 'Inter';

export const STICKY_COLORS: Record<string, string> = {
  yellow: '#fff2b2',
  orange: '#ffd9a8',
  red: '#ffc7c2',
  pink: '#ffc4f0',
  violet: '#dccbff',
  blue: '#c2e5ff',
  teal: '#c6f2ef',
  green: '#cdf4d3',
  gray: '#e6e6e6',
  white: '#ffffff',
};

function base(type: NodeType): NodeData {
  return {
    id: '',
    type,
    name: '',
    parent: null,
    index: 'V',
    x: 0,
    y: 0,
    width: 100,
    height: 100,
    rotation: 0,
    visible: true,
    locked: false,
    opacity: 1,
    fills: [],
    strokes: [],
    strokeWeight: 1,
    strokeAlign: 'INSIDE',
    effects: [],
    cornerRadius: 0,
  };
}

const TYPE_DEFAULTS: Partial<Record<NodeType, NodeProps>> = {
  DOCUMENT: { name: 'Document' },
  PAGE: { name: 'Page 1', backgroundColor: parseColor('#f5f5f5'), pageKind: 'DESIGN', width: 0, height: 0 },
  FRAME: { name: 'Frame', fills: [solid('#ffffff')], clipsContent: true, layoutMode: 'NONE' },
  COMPONENT: { name: 'Component', fills: [solid('#ffffff')], clipsContent: true, layoutMode: 'NONE' },
  INSTANCE: { name: 'Instance', clipsContent: true, layoutMode: 'NONE' },
  // FigJam's look (read back from FigJam): sections, shapes, connectors and sticky text
  SECTION: { name: 'Section', fills: [solid('#ffffff')], strokes: [solid('#e6e6e6')], strokeWeight: 2, cornerRadius: 12, width: 800, height: 600 },
  GROUP: { name: 'Group' },
  RECTANGLE: { name: 'Rectangle', fills: [solid('#d9d9d9')] },
  ELLIPSE: { name: 'Ellipse', fills: [solid('#d9d9d9')] },
  POLYGON: { name: 'Polygon', fills: [solid('#d9d9d9')], pointCount: 3 },
  STAR: { name: 'Star', fills: [solid('#d9d9d9')], pointCount: 5, innerRadius: 0.382 },
  LINE: { name: 'Line', strokes: [solid('#000000')], strokeAlign: 'CENTER', height: 0, strokeCap: 'NONE' },
  VECTOR: { name: 'Vector', strokes: [solid('#000000')], strokeAlign: 'CENTER', windingRule: 'NONZERO', path: '' },
  TEXT: {
    name: 'Text',
    fills: [solid('#000000')],
    characters: '',
    fontFamily: DEFAULT_FONT,
    fontSize: 16,
    fontWeight: 400,
    textAlign: 'LEFT',
    verticalAlign: 'TOP',
    textAutoResize: 'WIDTH_AND_HEIGHT',
    width: 0,
    height: 0,
  },
  IMAGE: { name: 'Image', fills: [] },
  STICKY: {
    name: 'Sticky',
    width: 240,
    height: 240,
    fills: [solid(STICKY_COLORS.yellow)],
    effects: [{ type: 'DROP_SHADOW', color: parseColor('#0000001f'), offset: { x: 0, y: 2 }, radius: 6 }],
    characters: '',
    fontFamily: DEFAULT_FONT,
    fontSize: 24,
    fontWeight: 500,
    letterSpacing: -0.176,
    textAlign: 'LEFT',
    verticalAlign: 'TOP',
    textColor: parseColor('#000000cc'),
  },
  SHAPE_WITH_TEXT: {
    name: 'Shape',
    width: 200,
    height: 200,
    shape: 'SQUARE',
    fills: [solid('#c2e5ff')],
    strokes: [solid('#3dadff')],
    strokeWeight: 4,
    strokeAlign: 'CENTER',
    characters: '',
    fontFamily: DEFAULT_FONT,
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 24,
    letterSpacing: -0.176,
    textAlign: 'CENTER',
    verticalAlign: 'CENTER',
    textColor: parseColor('#1e1e1e'),
  },
  STYLE: { name: 'Style', width: 0, height: 0, styleType: 'FILL' },
  VARIABLE_COLLECTION: { name: 'Collection', width: 0, height: 0, modes: [{ id: 'm1', name: 'Default' }], defaultModeId: 'm1' },
  VARIABLE: { name: 'Variable', width: 0, height: 0, resolvedType: 'COLOR', valuesByMode: {} },
  CONNECTOR: {
    name: 'Connector',
    strokes: [solid('#1e1e1e')],
    strokeWeight: 4,
    strokeAlign: 'CENTER',
    connectorType: 'ELBOW',
    startCap: 'NONE',
    endCap: 'ARROW',
    connectorStart: { x: 0, y: 0 },
    connectorEnd: { x: 100, y: 0 },
    characters: '',
    fontFamily: DEFAULT_FONT,
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 24,
    letterSpacing: -0.176,
    textColor: parseColor('#1e1e1e'),
  },
};

/** Create a full NodeData record for a type, applying defaults then overrides. */
export function makeNode(type: NodeType, props: NodeProps & { id?: string } = {}): NodeData {
  const node = { ...base(type), ...structuredClone(TYPE_DEFAULTS[type] ?? {}) } as NodeData;
  Object.assign(node, props);
  if (!node.id) node.id = newId();
  if (!node.name) node.name = type.charAt(0) + type.slice(1).toLowerCase();
  return node;
}

export const CONTAINER_TYPES: ReadonlySet<NodeType> = new Set([
  'DOCUMENT',
  'VARIABLE_COLLECTION',
  'PAGE',
  'FRAME',
  'GROUP',
  'SECTION',
  'COMPONENT',
  'INSTANCE',
]);

export const TEXT_BEARING_TYPES: ReadonlySet<NodeType> = new Set(['TEXT', 'STICKY', 'SHAPE_WITH_TEXT', 'CONNECTOR']);

export function isContainer(type: NodeType): boolean {
  return CONTAINER_TYPES.has(type);
}

export function isFrameLike(type: NodeType): boolean {
  return type === 'FRAME' || type === 'COMPONENT' || type === 'INSTANCE';
}
